// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useMemo } from 'react';
import { LegacyLayoutNode, LegacyTreeNode } from '../../../lib/tree';
import type { TreeEdge } from '../../../lib/validation';

interface SizeConfig {
  width: number;
  height: number;
  fontSize: number;
  hGap: number;
  vGap: number;
}

interface TreeConnectionsProps {
  layoutNodes: LegacyLayoutNode[];
  sizeConfig: SizeConfig;
  showProbabilities: boolean;
  showTransitionDetails?: boolean;
  getNodeData: (id: string) => LegacyTreeNode | null;
  labelOffset: (p0: { x: number; y: number }, p1: { x: number; y: number }, k?: number) => { x: number; y: number };
  edges?: TreeEdge[];
}

/**
 * Renders tree connections (edges) with optional probability and transition detail labels.
 * Assumes edges are canonical source for transition data (normalized by fromLegacy).
 *
 * Transition costs display as "€X.XX" in red.
 * Transition utility losses display as "-X.XXX QALY" in purple.
 */
export const TreeConnections: React.FC<TreeConnectionsProps> = ({
  layoutNodes,
  sizeConfig,
  showProbabilities,
  showTransitionDetails = false,
  getNodeData,
  labelOffset,
  edges = [],
}) => {
  const byId: Record<string, LegacyLayoutNode> = Object.fromEntries(layoutNodes.map(n => [n.id, n]));

  // Create edge lookup map for transition costs/utilities
  // Edges are the canonical source for transition data (normalized by fromLegacy)
  const edgeMap = useMemo(() => {
    const map = new Map<string, TreeEdge>();
    edges.forEach(edge => {
      map.set(`${edge.parentId}-${edge.childId}`, edge);
    });
    return map;
  }, [edges]);

  return (
    <>
      {layoutNodes.flatMap(node => {
        const nodeData = getNodeData(node.id);
        if (!nodeData || !nodeData.children) return null;
        return nodeData.children.map(child => {
          const childNode = byId[child.id];
          if (!childNode) return null;
          const startX = node.x + sizeConfig.width / 2;
          const startY = node.y + sizeConfig.height;
          const endX = childNode.x + sizeConfig.width / 2;
          const endY = childNode.y;
          const pathData = `M ${startX} ${startY} L ${endX} ${endY}`;

          const p0 = { x: startX, y: startY };
          const p1 = { x: endX, y: endY };

          const parentIsChance = node.type === 'chance';
          const edge = edgeMap.get(`${node.id}-${child.id}`);
          const probability = edge?.probability ?? child.p;
          const branchLabel = edge?.label || child.label;

          // Read transition data only from edges (canonical source, normalized by fromLegacy)
          const transitionCost = edge?.transition_cost;
          const transitionUtilityLoss = edge?.transition_utility_loss;

          // Label position: consistently above the line, midway along the branch
          // Use larger offset to position above the line
          const basePt = labelOffset(p0, p1, 20);

          // When both are shown, position transition details to the left of probability
          const showProb = showProbabilities && parentIsChance && typeof probability === 'number';
          const showTrans = showTransitionDetails && (transitionCost !== undefined || transitionUtilityLoss !== undefined);
          const showBoth = showProb && showTrans;
          const showLabel = branchLabel && branchLabel.trim().length > 0;

          // Calculate horizontal offset for transition details (to the left of probability)
          // Estimate transition details width and add spacing
          const estimatedTransWidth = showTrans ? 60 : 0; // Rough estimate for "€XX.XX" or "-X.XXX QALY"
          const spacing = 40; // Space between transition details and probability
          const transitionOffsetX = showBoth ? -(estimatedTransWidth / 2 + spacing) : 0;

          return (
            <g key={`${node.id}-${child.id}`}>
              <path
                d={pathData}
                stroke="var(--edge-neutral, rgba(255, 255, 255, 0.6))"
                strokeWidth={1.5}
                fill="none"
                vectorEffect="non-scaling-stroke"
                markerEnd="url(#arrow)"
                className="connection-line"
              />
              {/* Branch label - dominant, larger and bolder, positioned above probability */}
              {showLabel && (
                <text
                  className="edge-label branch-label"
                  x={basePt.x}
                  y={basePt.y - (showProb ? 22 : 0)}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  stroke="rgba(10,20,40,0.95)"
                  strokeWidth={4}
                  fill="rgba(255, 255, 255, 0.98)"
                  fontSize={sizeConfig.fontSize + 2}
                  fontWeight="700"
                >
                  {branchLabel}
                </text>
              )}
              {/* Probability - larger font, positioned above line, midway along branch */}
              {showProb && (
                <text
                  className="edge-label probability-label"
                  x={basePt.x}
                  y={basePt.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  stroke="rgba(10,20,40,0.9)"
                  strokeWidth={3}
                  fill="var(--decision-tree-text-secondary, #a7d486)"
                  fontSize={sizeConfig.fontSize + 6}
                  fontWeight="600"
                >
                  {(probability * 100).toFixed(0)}%
                </text>
              )}
              {showTrans && (
                <>
                  {transitionCost !== undefined && (
                    <text
                      className="edge-label transition-cost"
                      x={basePt.x + transitionOffsetX}
                      y={basePt.y - 8}
                      textAnchor="end"
                      stroke="rgba(10,20,40,0.9)"
                      strokeWidth={3}
                      fill="var(--color-error, #ff6b6b)"
                      fontSize={sizeConfig.fontSize - 2}
                    >
                      €{transitionCost.toFixed(2)}
                    </text>
                  )}
                  {transitionUtilityLoss !== undefined && (
                    <text
                      className="edge-label transition-utility"
                      x={basePt.x + transitionOffsetX}
                      y={basePt.y + 8}
                      textAnchor="end"
                      stroke="rgba(10,20,40,0.9)"
                      strokeWidth={3}
                      fill="var(--color-info, #9b59b6)"
                      fontSize={sizeConfig.fontSize - 2}
                    >
                      -{transitionUtilityLoss.toFixed(3)} QALY
                    </text>
                  )}
                </>
              )}
            </g>
          );
        }).filter(Boolean);
      }).filter(Boolean)}
    </>
  );
};

